import React, { useEffect, useState } from "react";
import { Button, Card, Checkbox, Col, Divider, Form, Input, InputNumber, Modal, Row, Space, Tabs, message, Select, Switch } from "antd";
import ReturnAddresses from "./ReturnAddresses";
import ServiceAccounts from "./ServiceAccounts";

// Firebase
import firebase from "firebase/compat/app";

import _ from "lodash";

import CompaniesTable from "./CompaniesTable";
import { stamp } from "../../../modules/helpers/audit";
import moment from "moment";

const { Search } = Input;

function UserForm({ form, companies }) {
  return (
    <Form layout="vertical" form={form}>
      <Form.Item
        name="company"
        label="Compte / Entreprise"
        rules={[{ required: true, message: "Veuillez selectionner un compte" }]}
      >
        <Select
          showSearch
          placeholder="Sélectionnez un compte"
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {_.sortBy(companies, "name").map((company) => (
            <Select.Option value={company.id} key={company.key}>
              {company.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="last_name"
        label="Nom"
        rules={[{ required: true, message: "Veuillez entrer un nom" }]}
      >
        <Input placeholder="Ex: Dupont" />
      </Form.Item>

      <Form.Item
        name="first_name"
        label="Prénom"
        rules={[{ required: true, message: "Veuillez entrer un prénom" }]}
      >
        <Input placeholder="Ex: Jean" />
      </Form.Item>

      <Form.Item
        name="email"
        label="Adresse email"
        rules={[
          { required: true, message: "Veuillez entrer un email" },
          { type: "email", message: "Veuillez entrer un email valide" },
        ]}
      >
        <Input placeholder="Ex: jean.dupont@gmail.com" />
      </Form.Item>
    </Form>
  );
}

function UserModal({ visible, onSubmit, onCancel, isFormLoading, setIsFormLoading, companies }) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible === true) {
      setIsFormLoading(false);
    }
  }, [visible]);

  return (
    <Modal
      visible={visible}
      title="Inviter un nouvel utilisateur"
      okText="Inviter"
      canceltext="Annuler"
      maskClosable={false}
      onCancel={() => {
        onCancel();
      }}
      onOk={() => {
        form
          .validateFields()
          .then((values) => {
            setIsFormLoading(true);
            onSubmit(values, form.resetFields);
          })
          .catch(() => {
            // Validation failed
          });
      }}
      okButtonProps={{ loading: isFormLoading }}
    >
      <UserForm form={form} companies={companies} />
    </Modal>
  );
}

const DEFAULT_SEND_OPTIONS = {
  types: { simple: true, recommande: true, electronique: true },
  printing: { color_option: { black_white: true, color: true }, face_option: { one_face: true, double_face: true } },
  simple: { recipients_type: { unique: true, multiple: true }, envelope: { "J+3": true, "J+4": true } },
  recommande: { receipt_aknowledge: { yes: true, no: true }, level: { R1: true, R2: true, R3: true } },
};

function SendOptionsTab({ options, setOptions }) {
  const countChecked = (group) => {
    if (!group) return 0;
    return Object.values(group).filter(Boolean).length;
  };

  const handleTypeChange = (type, checked) => {
    if (!checked && countChecked(options.types) <= 1) {
      message.warning("Au moins un type d'envoi doit être sélectionné");
      return;
    }
    setOptions((prev) => ({ ...prev, types: { ...prev.types, [type]: checked } }));
  };

  const handleOptionChange = (category, field, value, checked) => {
    if (!checked && countChecked(options[category]?.[field]) <= 1) {
      message.warning("Au moins une option doit être sélectionnée");
      return;
    }
    setOptions((prev) => ({
      ...prev,
      [category]: { ...prev[category], [field]: { ...prev[category]?.[field], [value]: checked } },
    }));
  };

  return (
    <>
      <Card title="Types d'envoi" size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical">
          <Checkbox checked={options.types?.simple} onChange={(e) => handleTypeChange("simple", e.target.checked)}>Lettre simple</Checkbox>
          <Checkbox checked={options.types?.recommande} onChange={(e) => handleTypeChange("recommande", e.target.checked)}>Lettre recommandée</Checkbox>
          <Checkbox checked={options.types?.electronique} onChange={(e) => handleTypeChange("electronique", e.target.checked)}>Envoi électronique</Checkbox>
        </Space>
      </Card>
      <Card title="Options d'impression" size="small" style={{ marginBottom: 16 }}>
        <Row gutter={24}>
          <Col span={12}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Couleur</div>
            <Space direction="vertical">
              <Checkbox checked={options.printing?.color_option?.black_white} onChange={(e) => handleOptionChange("printing", "color_option", "black_white", e.target.checked)}>Noir/Blanc</Checkbox>
              <Checkbox checked={options.printing?.color_option?.color} onChange={(e) => handleOptionChange("printing", "color_option", "color", e.target.checked)}>Couleur</Checkbox>
            </Space>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Impression</div>
            <Space direction="vertical">
              <Checkbox checked={options.printing?.face_option?.one_face} onChange={(e) => handleOptionChange("printing", "face_option", "one_face", e.target.checked)}>Recto seulement</Checkbox>
              <Checkbox checked={options.printing?.face_option?.double_face} onChange={(e) => handleOptionChange("printing", "face_option", "double_face", e.target.checked)}>Recto/verso</Checkbox>
            </Space>
          </Col>
        </Row>
      </Card>
      <Card title="Options Lettre simple" size="small" style={{ marginBottom: 16 }}>
        <Row gutter={24}>
          <Col span={12}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Destinataires</div>
            <Space direction="vertical">
              <Checkbox checked={options.simple?.recipients_type?.unique} onChange={(e) => handleOptionChange("simple", "recipients_type", "unique", e.target.checked)}>Unique</Checkbox>
              <Checkbox checked={options.simple?.recipients_type?.multiple} onChange={(e) => handleOptionChange("simple", "recipients_type", "multiple", e.target.checked)}>Multiples</Checkbox>
            </Space>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Délai</div>
            <Space direction="vertical">
              <Checkbox checked={options.simple?.envelope?.["J+3"]} onChange={(e) => handleOptionChange("simple", "envelope", "J+3", e.target.checked)}>J+3</Checkbox>
              <Checkbox checked={options.simple?.envelope?.["J+4"]} onChange={(e) => handleOptionChange("simple", "envelope", "J+4", e.target.checked)}>J+4</Checkbox>
            </Space>
          </Col>
        </Row>
      </Card>
      <Card title="Options Recommandé" size="small">
        <Row gutter={24}>
          <Col span={12}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Accusé de réception</div>
            <Space direction="vertical">
              <Checkbox checked={options.recommande?.receipt_aknowledge?.yes} onChange={(e) => handleOptionChange("recommande", "receipt_aknowledge", "yes", e.target.checked)}>Avec AR</Checkbox>
              <Checkbox checked={options.recommande?.receipt_aknowledge?.no} onChange={(e) => handleOptionChange("recommande", "receipt_aknowledge", "no", e.target.checked)}>Sans AR</Checkbox>
            </Space>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Niveau</div>
            <Space direction="vertical">
              <Checkbox checked={options.recommande?.level?.R1} onChange={(e) => handleOptionChange("recommande", "level", "R1", e.target.checked)}>R1</Checkbox>
              <Checkbox checked={options.recommande?.level?.R2} onChange={(e) => handleOptionChange("recommande", "level", "R2", e.target.checked)}>R2</Checkbox>
              <Checkbox checked={options.recommande?.level?.R3} onChange={(e) => handleOptionChange("recommande", "level", "R3", e.target.checked)}>R3</Checkbox>
            </Space>
          </Col>
        </Row>
      </Card>
    </>
  );
}

function CompanyModal({ visible, onSubmit, onCancel, isFormLoading, setIsFormLoading, values }) {
  const [form] = Form.useForm();
  const [sendOptions, setSendOptions] = useState(DEFAULT_SEND_OPTIONS);

  useEffect(() => {
    if (visible === true) {
      setIsFormLoading(false);
      setSendOptions(values?.sendOptions || DEFAULT_SEND_OPTIONS);
    }
  }, [visible]);

  useEffect(() => {
    form.resetFields();
  }, [form, values]);

  return (
    <Modal
      visible={visible}
      title={values ? "Modifier un groupe" : "Créer un groupe"}
      okText={values ? "Modifier" : "Créer"}
      cancelText="Annuler"
      maskClosable={false}
      width={800}
      onCancel={() => {
        onCancel();
      }}
      onOk={() => {
        form
          .validateFields()
          .then((formValues) => {
            setIsFormLoading(true);
            onSubmit({ ...formValues, sendOptions }, form.resetFields);
          })
          .catch(() => {
            // Validation failed
          });
      }}
      okButtonProps={{ loading: isFormLoading }}
    >
      <Tabs defaultActiveKey="info">
        <Tabs.TabPane tab="Informations" key="info">
          <Form layout="vertical" form={form} initialValues={values}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="Nom"
                  rules={[{ required: true, message: "Veuillez entrer un nom" }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="accounting_name"
                  label="Nom comptable"
                  tooltip="Si plusieurs clients ont le même nom comptable, leurs données seront groupées dans l'export comptable"
                >
                  <Input placeholder="Laissez vide pour utiliser le nom" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label="Email de contact"
                  rules={[
                    { required: true, message: "Veuillez entrer un email" },
                    { type: "email", message: "Veuillez entrer un email valide" },
                  ]}
                >
                  <Input placeholder="Ex: entreprise@email.com" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="phone" label="Téléphone">
                  <Input placeholder="Ex: 0123456789" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="address_line_1"
              label="Adresse ligne 1"
              rules={[{ required: true, message: "Veuillez entrer une adresse valide" }]}
            >
              <Input placeholder="Adresse postale, boîte postale..." />
            </Form.Item>

            <Form.Item name="address_line_2" label="Adresse ligne 2">
              <Input placeholder="Appartement, suite, unité, immeuble, étage, etc." />
            </Form.Item>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="postal_code"
                  label="Code postal"
                  rules={[{ required: true, message: "Veuillez entrer un code postal" }]}
                >
                  <Input placeholder="Ex: 75008" />
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item
                  name="city"
                  label="Ville"
                  rules={[{ required: true, message: "Veuillez entrer une ville" }]}
                >
                  <Input placeholder="Ex: Paris" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Options d'envoi" key="sendOptions">
          <SendOptionsTab options={sendOptions} setOptions={setSendOptions} />
        </Tabs.TabPane>

        <Tabs.TabPane tab="Adresses de retour" key="returnAddresses">
          <ReturnAddresses companyId={values?.id} />
        </Tabs.TabPane>

        <Tabs.TabPane tab="Paramètres" key="settings">
          <Form layout="vertical" form={form} initialValues={values}>
            <Form.Item name="force2FA" label="Forcer Solidoc Authenticator" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Divider />

            <Form.Item
              name="allowSubAccounts"
              label="Autoriser création de sous-comptes"
              valuePropName="checked"
              tooltip="Permet au client de créer lui-même des sous-comptes pour son entreprise"
            >
              <Switch />
            </Form.Item>

            <Form.Item
              name="maxSubAccounts"
              label="Nombre max de sous-comptes"
              tooltip="Laissez vide pour illimité"
            >
              <InputNumber min={1} max={50} placeholder="Illimité" style={{ width: "100%" }} />
            </Form.Item>
          </Form>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Accès API" key="apiAccess">
          <Form layout="vertical" form={form} initialValues={values}>
            <Form.Item
              name="api_access"
              label="Activer l'accès API"
              valuePropName="checked"
              tooltip="Option du client : ses administrateurs pourront créer des comptes de service et des clés d'API (Paramètres > Accès API). Couper l'option bloque immédiatement toutes ses clés."
            >
              <Switch />
            </Form.Item>
          </Form>

          <Divider />

          <ServiceAccounts companyId={values?.id} />
        </Tabs.TabPane>
      </Tabs>
    </Modal>
  );
}

export default function () {
  const db = firebase.firestore();
  const inviteUser = firebase.app().functions("europe-west1").httpsCallable("inviteUser");

  const [companies, setCompanies] = useState([]);
  const [displayedCompanies, setDisplayedCompanies] = useState([]);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [companyModalVisible, setCompanyModalVisible] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date().getTime());
  const [searchTerms, setSearchTerms] = useState("");
  const [searchArr, setSearchArr] = useState([]);

  useEffect(() => {
    if (searchTerms === "") {
      setDisplayedCompanies([...companies]);
    } else {
      const r = searchArr
        .filter((a) => new RegExp(searchTerms, "ig").test(a.key))
        .map((a) => a.value);

      setDisplayedCompanies(_.filter(companies, (company) => r.indexOf(company.id) !== -1));
    }
  }, [searchTerms, searchArr]);

  useEffect(() => {
    db.collection("users")
      .get()
      .then((usersSnapshot) => {
        const companiesObj = {};
        const search = [];

        if (usersSnapshot.docs.length) {
          usersSnapshot.docs.forEach((userSnap) => {
            const userData = userSnap.data();
            userData.last_invite = userData.last_invite || userData.created_at || moment();
            const user = {
              ...userData,
              company: userData.company.id,
              uid: userSnap.id,
              key: userSnap.id,
              last_invite: userData.isActive
                ? moment()
                : moment((userData.last_invite || userData.created_at).toDate()),
            };

            search.push({
              key: `${userData.email} ${userData.first_name} ${userData.last_name}`,
              value: userData.company.id,
            });

            if (companiesObj[user.company]) {
              companiesObj[user.company].push(user);
            } else {
              companiesObj[user.company] = [user];
            }
          });

          db.collection("companies")
            .get()
            .then((companiesSnapshot) => {
              const r = companiesSnapshot.docs.map((snap) => {
                const company = snap.data();

                search.push({
                  key: `${company.email} ${company.name}`,
                  value: snap.id,
                });

                return {
                  ...company,
                  ref: snap.ref,
                  users: companiesObj[snap.id],
                  key: snap.id,
                  id: snap.id,
                };
              });

              setCompanies(r);
              setDisplayedCompanies(r);

              setSearchArr(search);
            });
        }
      });
  }, [lastUpdate]);

  return (
    <>
      <h1>Comptes utilisateurs</h1>

      <Row>
        <Col span={24} style={{ display: "flex" }}>
          <div style={{ flexGrow: 1 }}>
            <Search
              placeholder="Recherche"
              onSearch={(value) => {
                setSearchTerms(value);
              }}
              onChange={(e) => {
                if (e.target.value === "") setSearchTerms("");
              }}
              style={{ paddingRight: 32, maxWidth: 500 }}
              enterButton
              allowClear
            />
          </div>
          <div>
            <Button
              type="primary"
              onClick={() => {
                setSelectedCompany(null);
                setCompanyModalVisible(true);
              }}
              style={{ marginRight: 8 }}
            >
              Créer un groupe
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setUserModalVisible(true);
              }}
            >
              Inviter un utilisateur
            </Button>
          </div>
        </Col>
      </Row>

      <CompaniesTable
        searchTerms={searchTerms}
        setLastUpdate={setLastUpdate}
        allCompanies={companies}
        companies={displayedCompanies}
        setSelectedCompany={setSelectedCompany}
        setCompanyModalVisible={setCompanyModalVisible}
      />

      <CompanyModal
        values={selectedCompany}
        visible={companyModalVisible}
        onCancel={() => {
          setCompanyModalVisible(false);
        }}
        isFormLoading={isFormLoading}
        setIsFormLoading={setIsFormLoading}
        onSubmit={(values, resetFields) => {
          if (selectedCompany) {
            db.collection("companies")
              .doc(selectedCompany.id)
              .set({ ...values, last_change: stamp() }, { merge: true })
              .then(() => {
                const clonedUsers = companies;
                clonedUsers.splice(_.findIndex(companies, { id: selectedCompany.id }), 1, {
                  ...selectedCompany,
                  ...values,
                });

                setCompanies([...clonedUsers]);
                setDisplayedCompanies([...clonedUsers]);

                resetFields();
                setCompanyModalVisible(false);
                message.success("Modifications enregistrées");
              })
              .catch((e) => {
                setIsFormLoading(false);
                message.error("Une erreur est survenue. Veuillez réessayer.");
                console.error(e);
              });
          } else {
            db.collection("companies")
              .add({ ...values, last_change: stamp() })
              .then(() => {
                setLastUpdate(new Date().getTime());
                resetFields();
                setCompanyModalVisible(false);
                message.success("Compte ajouté");
              })
              .catch((e) => {
                setIsFormLoading(false);
                message.error("Une erreur est survenue. Veuillez réessayer.");
                console.error(e);
              });
          }
        }}
      />

      <UserModal
        companies={companies}
        visible={userModalVisible}
        onCancel={() => {
          setUserModalVisible(false);
        }}
        isFormLoading={isFormLoading}
        setIsFormLoading={setIsFormLoading}
        onSubmit={(values, resetFields) => {
          inviteUser(values).then((response) => {
            const { data } = response;

            if (data.error) {
              const errorObj = data.data && data.data.errorInfo;

              const displayMessage =
                errorObj && errorObj.code === "auth/email-already-exists"
                  ? "Cette addresse email est déjà utilisée."
                  : "Une erreur est survenue. Veuillez réessayer.";

              setIsFormLoading(false);
              message.error(displayMessage);
            } else {
              resetFields();
              setUserModalVisible(false);
              setLastUpdate(new Date().getTime());
              message.success("Invitation envoyée");
            }
          });
        }}
      />
    </>
  );
}
